import React from 'react';
import { Link } from 'react-router-dom';
import Container from './Container';
import Logo from '../ui/Logo';
import { FuturisticButton } from '../ui/futuristic';
import { partners } from '../../data/mockData';

const FuturisticFooter: React.FC = () => {
  return (
    <footer className="bg-neu-bg pt-10 pb-8">
      <Container>
        {/* Top divider — extruded pill */}
        <div
          className="w-24 h-1 rounded-full mx-auto mb-10 bg-neu-bg"
          style={{
            boxShadow: '3px 3px 6px rgb(163,177,198,0.6), -3px -3px 6px rgba(255,255,255,0.5)',
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Logo size="md" />
            <p className="mt-4 text-neu-muted text-sm leading-relaxed max-w-sm">
              Empowering businesses and individuals to harness the power of AI through education,
              implementation, and optimization services.
            </p>

            {/* Social icons */}
            <div className="mt-6">
              <h3 className="text-sm font-display font-semibold text-neu-text mb-4">Connect with us</h3>
              <div className="flex space-x-3">
                {[
                  {
                    href: 'https://linkedin.com/company/CodeMyAI',
                    label: 'LinkedIn',
                    icon: (
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    ),
                  },
                  {
                    href: 'https://github.com/CodeMyAI',
                    label: 'GitHub',
                    icon: (
                      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                    ),
                  },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={[
                      'flex items-center justify-center w-10 h-10 rounded-full bg-neu-bg text-neu-muted',
                      'hover:text-neu-accent transition-all duration-200',
                      'shadow-[5px_5px_10px_rgb(163,177,198,0.6),-5px_-5px_10px_rgba(255,255,255,0.5)]',
                      'hover:shadow-[7px_7px_12px_rgb(163,177,198,0.7),-7px_-7px_12px_rgba(255,255,255,0.6)]',
                      'active:shadow-[inset_3px_3px_6px_rgb(163,177,198,0.6),inset_-3px_-3px_6px_rgba(255,255,255,0.5)]',
                    ].join(' ')}
                  >
                    <span className="sr-only">{social.label}</span>
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      {social.icon}
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-display font-semibold text-neu-text mb-4">Services</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
              {[
                { label: 'AI Training', path: '/services/ai-training' },
                { label: 'AI Onboarding', path: '/services/ai-onboarding' },
                { label: 'Workflow Automation', path: '/services/workflow-automation' },
                { label: 'CRM & Pipeline', path: '/services/crm-pipeline-automation' },
                { label: 'Custom AI Dev', path: '/services/custom-ai-development' },
                { label: 'AI Optimisation', path: '/services/ai-optimization' },
                { label: 'Web Design', path: '/services/web-design' },
                { label: 'Software Dev', path: '/services/software-development' },
                { label: 'SEO Services', path: '/services/seo' },
                { label: 'Digital Marketing', path: '/services/digital-marketing' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-neu-muted hover:text-neu-accent transition-colors duration-200 no-underline font-display"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions & Resources */}
          <div>
            <h3 className="text-sm font-display font-semibold text-neu-text mb-4">Solutions</h3>
            <ul className="space-y-2.5 mb-6">
              {[
                { label: 'For Individuals', path: '/solutions/individuals' },
                { label: 'For Small Businesses', path: '/solutions/small-business' },
                { label: 'For Startups', path: '/solutions/startups' },
                { label: 'Industry Solutions', path: '/solutions/industries' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-neu-muted hover:text-neu-accent transition-colors duration-200 no-underline font-display">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-display font-semibold text-neu-text mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:Hello@CodeMyAI.com"
                  className="flex items-start gap-2 text-sm text-neu-muted hover:text-neu-accent transition-colors duration-200 no-underline font-display"
                >
                  <svg className="h-5 w-5 mt-0.5 flex-shrink-0 text-neu-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Hello@CodeMyAI.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+4407415928896"
                  className="flex items-start gap-2 text-sm text-neu-muted hover:text-neu-accent transition-colors duration-200 no-underline font-display"
                >
                  <svg className="h-5 w-5 mt-0.5 flex-shrink-0 text-neu-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +44 07415 928896
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-neu-muted font-display">
                <svg className="h-5 w-5 mt-0.5 flex-shrink-0 text-neu-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>31 Giles Drive<br />London, UK</span>
              </li>
            </ul>
            <div className="mt-5">
              <FuturisticButton
                variant="accent"
                size="sm"
                onClick={() => (window.location.href = '/book')}
              >
                Book a Free Call
              </FuturisticButton>
            </div>
          </div>
        </div>

        {/* Partners Section */}
        <div className="mt-8 pt-6 border-t" style={{ borderTopColor: 'rgba(163,177,198,0.3)' }}>
          <p className="text-center text-xs text-neu-muted mb-4 font-semibold uppercase tracking-wider">Our Partners</p>
          <div className="flex flex-wrap justify-center items-center gap-6">
            {partners.map((partner, index) => (
              <div key={index} className="h-8 flex items-center justify-center hover:scale-110 transition-transform duration-300">
                <img
                  src={partner.logoSrc}
                  alt={partner.name}
                  className="max-h-8 max-w-20 object-contain"
                  title={partner.name}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <div
          className="mt-10 pt-6 text-center text-neu-muted text-sm font-display"
          style={{
            borderTop: '1px solid rgba(163,177,198,0.3)',
          }}
        >
          <p>© {new Date().getFullYear()} CodeMyAI. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/legal/privacy-policy" className="hover:text-neu-accent transition-colors duration-200 no-underline">
              Privacy Policy
            </Link>
            <Link to="/legal/terms-of-service" className="hover:text-neu-accent transition-colors duration-200 no-underline">
              Terms of Service
            </Link>
            <Link to="/legal/cookies" className="hover:text-neu-accent transition-colors duration-200 no-underline">
              Cookies
            </Link>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default FuturisticFooter;
